import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Boxes, Laptop, Monitor, Keyboard, Cable, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * "My Assets" — compact strip showing whatever's been assigned to the current
 * user in the asset register. Lives at the top of My HR so people stop pinging
 * Slack for serial numbers. Hidden when no assets are assigned.
 *
 * Pulls from `GET /api/assets?assigned_to=<my-user-id>` which is already
 * cheap (assets is a tiny collection).
 */

// Heuristic: pick an icon based on the asset name. Falls back to Boxes.
function iconFor(name) {
  const s = (name || "").toLowerCase();
  if (s.includes("laptop") || s.includes("macbook") || s.includes("notebook")) return Laptop;
  if (s.includes("monitor") || s.includes("display") || s.includes("screen")) return Monitor;
  if (s.includes("keyboard")) return Keyboard;
  if (s.includes("cable") || s.includes("adapter") || s.includes("dock") || s.includes("hub")) return Cable;
  return Boxes;
}

export default function MyAssetsStrip() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get("/assets", { params: { assigned_to: user.id } })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, [user?.id]);

  if (!loaded || rows.length === 0) return null;

  return (
    <div className="card-flat p-4 mb-5" data-testid="my-assets-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Boxes size={14} className="text-slate-500" />
          <div className="label-overline">My assets · {rows.length}</div>
        </div>
        <Link to="/my-hr?tab=assets" className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="my-assets-open-register">
          View register <ExternalLink size={10} />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {rows.map((r) => {
          const Icon = iconFor(r.name);
          return (
            <div key={r.id} className="rounded-sm border border-slate-200 p-3 hover:border-slate-300 transition-colors bg-white" data-testid={`my-asset-${r.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-7 h-7 rounded-sm bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                  <Icon size={13} />
                </span>
                <div className="font-medium text-xs text-slate-900 truncate" title={r.name}>{r.name}</div>
              </div>
              {r.serial_number && (
                <div className="text-[10px] text-slate-500 font-mono truncate" title={r.serial_number}>SN · {r.serial_number}</div>
              )}
              {r.category && (
                <div className="text-[10px] text-slate-400 capitalize mt-0.5">{r.category}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
